import type { BrandSettings } from '../services/brand-settings';
import { getFontFamilyCSS } from '../services/brand-settings';
import { BOOKING_TEBOX_BODY_COLOR, BOOKING_TEBOX_DEFAULT_FONT } from '../styles/booking-typography';
import { bookingBrandInfoChromeStyles, bookingTebox, teboxDesignSystem } from '../styles/tebox-booking';

export type BookingBrandColors = {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  buttonTextColor: string;
  primaryTextColor: string;
  headingTextBold: boolean;
  headingTextItalic: boolean;
  headerColor: string;
  borderColor: string;
  fontFamily: string;
  borderRadius: string;
  logoUrl?: string;
};

/** Hero heading typography on tee cards (time + price numerals). */
export function headingTextStyle(
  brand: Pick<BookingBrandColors, 'headingTextBold' | 'headingTextItalic'>,
): { fontWeight: 500 | 700; fontStyle: 'normal' | 'italic' } {
  return {
    fontWeight: brand.headingTextBold ? 700 : 500,
    fontStyle: brand.headingTextItalic ? 'italic' : 'normal',
  };
}

export const BOOKING_HEADING_TEXT_WEIGHT_VAR = 'var(--brand-heading-text-weight, 500)';
export const BOOKING_HEADING_TEXT_STYLE_VAR = 'var(--brand-heading-text-style, normal)';

/** White modal panels — page `textColor` is often light-on-dark; use TEBOX ink for legibility. */
export function resolveBookingModalPanelInk() {
  return {
    title: bookingTebox.colors.textPrimary,
    body: bookingTebox.colors.textPrimary,
    muted: bookingTebox.colors.textSecondary,
    panelBg: '#FFFFFF',
  };
}

export function buildBookingLoginModalStyles(brand: BookingBrandColors) {
  const surface = buildBookingSurfaceStyles(brand);
  const ink = resolveBookingModalPanelInk();
  const fontFamily = getFontFamilyCSS(brand.fontFamily);
  const cardChrome = bookingBrandInfoChromeStyles({
    primaryColor: brand.primaryColor,
    buttonColor: brand.buttonColor,
  });

  return {
    fontFamily,
    cardRadius: brand.borderRadius,
    innerRadius: teboxDesignSystem.borderRadius.md,
    panelBg: ink.panelBg,
    hairline: brand.borderColor,
    ink,
    surface,
    cardChrome,
    accent: brand.buttonColor || brand.primaryColor,
    primaryText: brand.primaryTextColor || brand.buttonTextColor || '#FFFFFF',
    socialButton: {
      color: ink.body,
      borderColor: brand.borderColor,
      backgroundColor: ink.panelBg,
      hoverBorderColor: `color-mix(in srgb, ${brand.primaryColor} 18%, ${brand.borderColor})`,
    },
  };
}

export function resolveBookingBrandColors(
  settings?: Partial<BrandSettings> | null,
): BookingBrandColors {
  return {
    primaryColor: settings?.primaryColor || '#000000',
    secondaryColor: settings?.secondaryColor || bookingTebox.colors.textSecondary,
    accentColor: settings?.accentColor || '#000000',
    backgroundColor: settings?.backgroundColor || '#FFFFFF',
    textColor: settings?.textColor || BOOKING_TEBOX_BODY_COLOR,
    buttonColor: settings?.buttonColor || '#000000',
    buttonTextColor: settings?.buttonTextColor || '#FFFFFF',
    primaryTextColor:
      settings?.primaryTextColor || settings?.buttonTextColor || '#FFFFFF',
    headingTextBold: settings?.headingTextBold ?? false,
    headingTextItalic: settings?.headingTextItalic ?? false,
    headerColor: settings?.headerColor || bookingTebox.colors.bgPrimary,
    borderColor: settings?.borderColor || bookingTebox.colors.border,
    fontFamily: settings?.fontFamily || BOOKING_TEBOX_DEFAULT_FONT,
    borderRadius:
      settings?.borderRadius && settings.borderRadius !== '0px'
        ? settings.borderRadius
        : teboxDesignSystem.borderRadius.lg,
    logoUrl: settings?.logoUrl?.trim() || undefined,
  };
}

export function buildBookingSurfaceStyles(brand: BookingBrandColors) {
  const cardChrome = bookingBrandInfoChromeStyles({
    primaryColor: brand.primaryColor,
    buttonColor: brand.buttonColor,
  });

  const fontFamily = 'var(--brand-font)';

  return {
    page: {
      backgroundColor: brand.backgroundColor,
      color: brand.textColor,
      fontFamily,
    },
    heading: {
      fontFamily,
      fontWeight: 500 as const,
      color: brand.textColor,
      margin: 0,
    },
    body: {
      fontFamily,
      color: brand.textColor,
    },
    muted: {
      fontFamily,
      fontSize: teboxDesignSystem.typography.caption.fontSize,
      lineHeight: teboxDesignSystem.typography.caption.lineHeight,
      color: brand.secondaryColor,
    },
    card: {
      ...cardChrome,
      borderRadius: brand.borderRadius,
    },
    input: {
      fontFamily,
      color: brand.textColor,
      backgroundColor: brand.backgroundColor,
      border: `1px solid ${brand.borderColor}`,
      borderRadius: brand.borderRadius,
    },
    outlineButton: {
      fontFamily,
      color: brand.textColor,
      backgroundColor: cardChrome.backgroundColor,
      border: cardChrome.border,
      borderRadius: brand.borderRadius,
      cursor: 'pointer' as const,
    },
    primaryButton: {
      fontFamily,
      color: brand.primaryTextColor,
      backgroundColor: brand.buttonColor,
      border: 'none',
      borderRadius: brand.borderRadius,
      cursor: 'pointer' as const,
      fontWeight: 500,
    },
    divider: {
      borderColor: brand.borderColor,
    },
  };
}
